import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import Sidebar from './Sidebar';
import { useState, useEffect, useRef, useMemo } from 'react';
import { useAlarmBadge } from '../context/AlarmBadgeContext';
import { getNotificationsPageResolved } from '../config';

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const { language, setLanguage, t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [theme, setTheme] = useState('dark');
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);

  useEffect(() => {
    const savedTheme = localStorage.getItem('dio-theme') || 'dark';
    setTheme(savedTheme);
    if (savedTheme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, []);

  // F8 = temë (dark/light), F6 = gjuhë (sq/en) – pa butona në header
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'F8') {
        e.preventDefault();
        setTheme((prev) => {
          const next = prev === 'dark' ? 'light' : 'dark';
          localStorage.setItem('dio-theme', next);
          document.documentElement.classList.toggle('dark', next === 'dark');
          return next;
        });
      }
      if (e.key === 'F6') {
        e.preventDefault();
        setLanguage(language === 'sq' ? 'en' : 'sq');
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [setLanguage, language]);

  // Mbyll menynë e userit kur klikohet jashtë
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    };
    if (userMenuOpen) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [userMenuOpen]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (path) => location.pathname === path;

  // Nëse përdoruesi ka allowed_routes, shfaq vetëm ato linqe në navigim.
  const allowedPaths = user?.allowed_routes && Array.isArray(user.allowed_routes) && user.allowed_routes.length > 0
    ? user.allowed_routes
    : null;

  const filterByAllowed = (items) => {
    if (allowedPaths === null) return items;
    return items.filter((item) => allowedPaths.includes(item.path));
  };

  const showAlarmBell = !!user;
  const alarmBadgeCount = useAlarmBadge();
  const notifTarget = useMemo(() => getNotificationsPageResolved(), []);

  // Mobile Bottom Navigation – vetëm Dashboard, Device, Me
  const mobileNavItems = [
    { 
      path: '/dashboard', 
      labelKey: 'home',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
        </svg>
      )
    },
    { 
      path: '/devices', 
      labelKey: 'devices',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
        </svg>
      )
    },
    { 
      path: '/me', 
      labelKey: 'me',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
      )
    },
  ];


  return (
    <div className="flex h-screen bg-gray-50 dark:bg-gray-900">
      {/* Desktop Sidebar */}
      <div className="hidden md:block">
        <Sidebar />
      </div>
      
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="bg-white dark:bg-gray-800 shadow-sm z-10">
          <div className="flex items-center justify-between px-4 md:px-4 py-2 md:py-3">
            <div className="flex items-center space-x-3">
              {/* Logo DIO IOT: logo-dio-iot.png = blu/ngjyrë (light mode), logo-dio-iot-white.png = e bardhë (dark mode) */}
              <img 
                src={`${process.env.PUBLIC_URL || ''}/assets/images/logo-dio-iot.png`} 
                alt="DIO IOT" 
                className="h-7 md:h-8 dark:hidden object-contain"
                onError={(e) => { e.target.onerror = null; e.target.src = `${process.env.PUBLIC_URL || ''}/assets/images/logo-full.svg`; }}
              />
              <img 
                src={`${process.env.PUBLIC_URL || ''}/assets/images/logo-dio-iot-white.png`} 
                alt="" 
                className="h-7 md:h-8 hidden dark:block object-contain"
                onError={(e) => { e.target.onerror = null; e.target.src = `${process.env.PUBLIC_URL || ''}/assets/images/logo-white-full.svg`; }}
              />
            </div>
            
            <div className="flex items-center space-x-1 md:space-x-3" ref={userMenuRef}>
              {showAlarmBell && (
                (() => {
                  const bellClass =
                    'relative flex items-center justify-center w-10 h-10 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800/80 text-gray-700 dark:text-gray-200 shadow-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition shrink-0';
                  const inner = (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                      </svg>
                      {alarmBadgeCount > 0 && (
                        <span className="absolute -top-0.5 -right-0.5 min-w-[1.125rem] h-[1.125rem] px-0.5 flex items-center justify-center rounded-full bg-red-600 text-white text-[10px] font-bold leading-none ring-2 ring-white dark:ring-gray-800">
                          {alarmBadgeCount > 99 ? '99+' : alarmBadgeCount}
                        </span>
                      )}
                    </>
                  );
                  if (notifTarget.kind === 'link') {
                    return (
                      <Link to={notifTarget.to} className={bellClass} aria-label={t('notification')} title={t('notification')}>
                        {inner}
                      </Link>
                    );
                  }
                  return (
                    <a
                      href={notifTarget.href}
                      className={bellClass}
                      aria-label={t('notification')}
                      title={t('notification')}
                      {...(notifTarget.external ? { rel: 'noopener noreferrer' } : {})}
                    >
                      {inner}
                    </a>
                  );
                })()
              )}
              {/* User: kliko → shfaqet Dil/Logout */}
              <div className="hidden md:block relative">
                <button
                  type="button"
                  onClick={() => setUserMenuOpen((o) => !o)}
                  className="flex flex-col items-end text-right px-2 py-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition"
                  aria-expanded={userMenuOpen}
                  aria-haspopup="true"
                >
                  <p className="text-xs font-semibold text-gray-900 dark:text-white">
                    {user?.first_name} {user?.last_name}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {user?.role}
                  </p>
                </button>
                {userMenuOpen && (
                  <div className="absolute right-0 top-full mt-1 py-1 min-w-[120px] bg-white dark:bg-gray-700 rounded-lg shadow-lg border border-gray-200 dark:border-gray-600 z-50">
                    <button
                      type="button"
                      onClick={() => {
                        setUserMenuOpen(false);
                        handleLogout();
                      }}
                      className="w-full text-left px-3 py-2 text-xs font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                    >
                      {t('logout')}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1 overflow-y-auto pb-16 md:pb-0">
          {children}
        </main>

        {/* Mobile Bottom Navigation – vetëm Dashboard, Device, Me */}
        <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-800 z-50 safe-area-bottom">
          <div className="flex justify-around items-center h-14">
            {filterByAllowed(mobileNavItems).map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex flex-col items-center justify-center flex-1 h-full transition-all duration-200 ease-out active:scale-95 hover:-translate-y-0.5 active:translate-y-0 ${
                  isActive(item.path)
                    ? 'text-blue-600 dark:text-blue-400'
                    : 'text-gray-500 dark:text-gray-400'
                }`}
              >
                {item.icon}
                <span className="text-[10px] mt-0.5 font-medium">{item.labelKey ? t(item.labelKey) : item.label}</span>
              </Link>
            ))}
          </div>
        </nav>
      </div>
    </div>
  );
};

export default Layout;
